import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import PayerCreateForm from '@/components/PayerCreateForm';
import { ApiError } from '@/lib/api';
import {
  type TPartyChoice,
  type TSettlePreview,
  useSettleIssue,
  useSettlePreview,
} from '@/models/invoices';
import type { TInvoice } from '@/types';
import { issueGate, settleRefusal } from './settleDialog';

const PRINTED = 'printed';

const choiceOf = (key: string): TPartyChoice =>
  key === PRINTED
    ? { kind: 'printed' }
    : { kind: 'payer', payerId: Number(key.replace('payer:', '')) };

const money = (n: number, currency: string) => `${n.toFixed(2)} ${currency}`;

/** How long accounting has to take an Issue back before it is sent. */
const UNDO_SECONDS = 10;

/**
 * Settle & issue invoice: the final invoice exactly as it will be stored, with the only two
 * things accounting may choose - the paying party and the delivery email. Issue checks the
 * preview again on the server and refuses when anything moved; nothing is sent.
 */
export function SettleIssueModal({
  invoice,
  opened,
  onClose,
}: {
  invoice: TInvoice;
  opened: boolean;
  onClose: () => void;
}) {
  const [, navigate] = useLocation();
  const previewMutation = useSettlePreview();
  const issueMutation = useSettleIssue();
  const [preview, setPreview] = useState<TSettlePreview | null>(null);
  const [partyKey, setPartyKey] = useState(PRINTED);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<{
    tone: 'red' | 'orange' | 'blue';
    title: string;
    message: string;
  } | null>(null);
  const requestKey = useRef('');
  // Only the countdown holds an unsent Issue, in memory alone: leaving the screen, hiding the
  // tab or losing the connection cancels it, and nothing is sent through unload.
  const [remaining, setRemaining] = useState<number | null>(null);
  const timer = useRef<number | null>(null);
  const stopCountdown = useCallback(() => {
    if (timer.current != null) clearInterval(timer.current);
    timer.current = null;
    setRemaining(null);
  }, []);
  useEffect(() => stopCountdown, [stopCountdown]);
  useEffect(() => {
    if (remaining == null) return;
    const cancel = () => stopCountdown();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') cancel();
    };
    document.addEventListener('visibilitychange', onVisibility);
    globalThis.addEventListener('pagehide', cancel);
    globalThis.addEventListener('offline', cancel);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      globalThis.removeEventListener('pagehide', cancel);
      globalThis.removeEventListener('offline', cancel);
    };
  }, [remaining, stopCountdown]);

  const load = useCallback(
    async (key: string, deliveryEmail?: string | null, confirm = false) => {
      setEmailError(null);
      try {
        const next = await previewMutation.mutateAsync({
          id: invoice.id,
          party: choiceOf(key),
          ...(deliveryEmail !== undefined ? { deliveryEmail } : {}),
          confirmDifference: confirm,
        });
        setPreview(next);
        if (deliveryEmail === undefined) setEmail(next.deliveryEmail ?? '');
        return next;
      } catch (error) {
        const body = error instanceof ApiError ? error.body : String(error);
        if (deliveryEmail !== undefined && /email/i.test(body)) {
          setEmailError(body);
        } else {
          setNotice({ tone: 'red', title: 'No preview', message: body });
        }
        return null;
      }
    },
    [invoice.id, previewMutation],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloads only when the dialog opens
  useEffect(() => {
    if (!opened) return;
    requestKey.current = crypto.randomUUID();
    setPreview(null);
    setPartyKey(PRINTED);
    setEmailTouched(false);
    setNotice(null);
    setCreating(false);
    void load(PRINTED);
  }, [opened, invoice.id]);

  const busy = previewMutation.isPending || issueMutation.isPending;
  const emailPending =
    preview != null && email.trim() !== (preview.deliveryEmail ?? '');
  const confirmed = preview?.differenceConfirmed ?? false;
  const gate = issueGate(preview, { confirmed, busy });

  // The confirmation is part of the signed preview: the server shows the invoice again with it,
  // and a fresh preview after any change starts unconfirmed.
  const confirmDifference = (checked: boolean) => {
    stopCountdown();
    void load(partyKey, emailTouched ? email.trim() : undefined, checked);
  };

  const changeParty = (key: string | null) => {
    if (!key) return;
    stopCountdown();
    setPartyKey(key);
    void load(key, emailTouched ? email.trim() : undefined);
  };

  const applyEmail = () => {
    if (!preview || !emailPending) return;
    stopCountdown();
    setEmailTouched(true);
    void load(partyKey, email.trim());
  };

  const issue = async () => {
    if (!preview || !gate.enabled || emailPending) return;
    setNotice(null);
    try {
      const res = await issueMutation.mutateAsync({
        id: invoice.id,
        intent: preview.intent,
        requestKey: requestKey.current,
      });
      notifications.show({
        color: 'green',
        title: `${invoice.invoiceNumber} settled`,
        message: `Invoice ${res.finalInvoice.number} issued. It was not sent: send it from its page.`,
      });
      onClose();
      navigate(`~/invoices/final/${res.finalInvoice.id}`);
    } catch (error) {
      if (!(error instanceof ApiError) || error.status >= 500) {
        setNotice({
          tone: 'orange',
          title: "We didn't get a response",
          message:
            'The invoice may or may not have been issued. Press Issue again: the same request returns the same invoice, never a second one.',
        });
        return;
      }
      const refusal = settleRefusal<TSettlePreview>(error.body);
      if (refusal.kind === 'stale') {
        setPreview(refusal.preview);
        setEmail(refusal.preview.deliveryEmail ?? '');
        setNotice({
          tone: 'orange',
          title: 'The preview was out of date',
          message: `${refusal.message} Nothing was issued.`,
        });
      } else if (refusal.kind === 'issued') {
        onClose();
        navigate(`~/invoices/final/${refusal.finalInvoiceId}`);
      } else {
        setNotice({
          tone: 'red',
          title: 'Not issued',
          message: refusal.message,
        });
      }
    }
  };

  const issueRef = useRef(issue);
  issueRef.current = issue;
  const startCountdown = () => {
    if (!preview || !gate.enabled || emailPending) return;
    setNotice(null);
    let left = UNDO_SECONDS;
    setRemaining(left);
    timer.current = globalThis.setInterval(() => {
      left -= 1;
      if (left > 0) {
        setRemaining(left);
        return;
      }
      stopCountdown();
      void issueRef.current();
    }, 1000);
  };
  const close = () => {
    if (issueMutation.isPending) return;
    stopCountdown();
    onClose();
  };

  const options = preview?.partyOptions ?? null;
  const partyData = [
    {
      value: PRINTED,
      label: `Proforma's party (as printed)${
        preview && partyKey === PRINTED ? ` - ${preview.party.name}` : ''
      }`,
    },
    ...(options ?? []).map((o) => ({
      value: `payer:${o.payerId}`,
      label: `${o.name}${o.kycValid ? '' : ' (not verified)'}`,
    })),
  ];

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={`Settle ${invoice.invoiceNumber} & issue invoice`}
      size="xl"
      centered
      closeOnClickOutside={!issueMutation.isPending}
      closeOnEscape={!issueMutation.isPending}
    >
      <Stack gap="md">
        {preview ? (
          <Group gap="lg">
            <Text size="sm">
              Due: <b>{money(preview.due, preview.currency)}</b>
            </Text>
            <Text size="sm">
              Collected: <b>{money(preview.collected, preview.currency)}</b>
            </Text>
            {preview.difference !== 0 && (
              <Badge color="orange" variant="light">
                Difference {preview.difference > 0 ? '+' : ''}
                {preview.difference.toFixed(2)} {preview.currency}
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              From proforma version {preview.source.version} - issue date{' '}
              {preview.issueDate}
            </Text>
          </Group>
        ) : (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm">Preparing the preview</Text>
          </Group>
        )}

        <Group align="flex-end" grow>
          <Select
            label="Paying party"
            description={
              options == null
                ? "Choosing another party needs the permission to manage this account's payers"
                : 'A payer of this account; the invoice keeps the proforma amounts'
            }
            data={partyData}
            value={partyKey}
            onChange={changeParty}
            disabled={busy || options == null}
            allowDeselect={false}
          />
          <TextInput
            label="Delivery email"
            description="Where Send will deliver it. Leave empty to issue without one."
            value={email}
            onChange={(e) => {
              stopCountdown();
              setEmail(e.currentTarget.value);
            }}
            onBlur={applyEmail}
            onKeyDown={(e) => e.key === 'Enter' && applyEmail()}
            error={emailError}
            disabled={busy}
          />
        </Group>

        {options != null && !creating && (
          <Group>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => setCreating(true)}
            >
              New payer for this account
            </Button>
          </Group>
        )}
        {creating && preview && (
          <Box
            p="sm"
            style={{ border: '1px solid var(--mantine-color-gray-3)' }}
          >
            <PayerCreateForm
              accountId={preview.proforma.accountId}
              onCancel={() => setCreating(false)}
              onCreated={(payer) => {
                setCreating(false);
                changeParty(`payer:${payer.id}`);
              }}
            />
          </Box>
        )}

        {preview?.printedPartyOutdated && partyKey === PRINTED && (
          <Text size="xs" c="dimmed">
            This payer's details changed since the proforma was issued. Choose
            it in the list to bill its current details.
          </Text>
        )}

        {preview?.warnings.map((w) => (
          <Alert key={w.code} color="orange" variant="light" p="xs">
            <Text size="sm">{w.message}</Text>
          </Alert>
        ))}

        {gate.needsConfirmation && (
          <Checkbox
            label="I confirm the invoice is issued for the proforma's amount although the collected amount differs"
            checked={confirmed}
            onChange={(e) => confirmDifference(e.currentTarget.checked)}
            disabled={busy}
          />
        )}

        {notice && (
          <Alert color={notice.tone} title={notice.title}>
            {notice.message}
          </Alert>
        )}

        {preview && (
          <Box style={{ border: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="xs" c="dimmed" p={4}>
              Preview - the number is assigned on issue
            </Text>
            <iframe
              title="Invoice preview"
              srcDoc={preview.html}
              sandbox=""
              style={{
                width: '100%',
                height: 520,
                border: 0,
                background: '#fff',
              }}
            />
          </Box>
        )}

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            Issuing settles the proforma and cannot be undone afterwards; you
            have
            {UNDO_SECONDS} seconds to undo. Nothing is sent.
          </Text>
          {remaining != null ? (
            <Group>
              <Text size="sm">
                Issuing in {remaining} second{remaining === 1 ? '' : 's'}
              </Text>
              <Button color="red" variant="light" onClick={stopCountdown}>
                Undo
              </Button>
            </Group>
          ) : (
            <Group>
              <Button
                variant="subtle"
                onClick={close}
                disabled={issueMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                color="teal"
                onClick={startCountdown}
                loading={issueMutation.isPending}
                disabled={!gate.enabled || emailPending}
                title={
                  emailPending
                    ? 'Apply the email first'
                    : (gate.reason ?? undefined)
                }
              >
                Issue invoice
              </Button>
            </Group>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
